"use client"

import { useEffect, useRef, useState } from "react"
import { Loader2, Pencil, Search, Trash2 } from "lucide-react"

import { RichTextEditor } from "./rich-text-editor"
import {
  useJobHeaderTitle,
  useSetJobHeaderAction,
} from "@/components/jobs/job-header-action"
import { useJobNotes, type JobNote } from "@/hooks/use-job-notes"
import { cn } from "@/lib/utils"
import { RichText } from "@/components/graphite/rich-text"

/**
 * Notes on a job, from canvas artboards 18a and 18b, with 18f/18g on white.
 *
 * "The card is gone. A screen inside a screen needs a title, which is why the
 * old one said 'Notes' in the bar and 'Job Notes' again 40px below, then
 * explained itself in a third line. Here the bar carries the job, the notes
 * carry the content, and hairlines carry the structure. New note is a quiet
 * teal label in the bar, not a filled pill floating in a header."
 *
 * And 18b: "the empty state *is* the composer." A job with no notes has exactly
 * one thing to do, so it opens the field rather than drawing a glyph in a box
 * and quoting the button next to it.
 */

const META = "font-mono text-[10.5px] leading-none text-muted-foreground"

interface JobNotesProps {
  jobId: string
  jobTitle: string
}

export function JobNotes({ jobId }: JobNotesProps) {
  const notes = useJobNotes(jobId)
  const [composing, setComposing] = useState(false)
  const [draft, setDraft] = useState("")
  const [editing, setEditing] = useState<JobNote | null>(null)
  const [searching, setSearching] = useState(false)

  /*
    A job with no notes opens straight into the composer — but only once, and
    only while it really is empty, so dismissing the composer on an empty job
    does not immediately reopen it.
  */
  const openedForEmpty = useRef(false)
  useEffect(() => {
    if (notes.isLoading || !notes.isEmpty || openedForEmpty.current) return
    openedForEmpty.current = true
    setComposing(true)
  }, [notes.isLoading, notes.isEmpty])

  const isWriting = composing || editing !== null
  const body = editing ? editing.body : draft

  const close = () => {
    setComposing(false)
    setEditing(null)
    setDraft("")
  }

  const save = async () => {
    const saved = editing
      ? await notes.update(editing.id, editing.body)
      : await notes.create(draft)
    if (saved) close()
  }

  useJobHeaderTitle(isWriting ? (editing ? "Edit note" : "New note") : null)

  // 18a puts the action in the bar rather than in a header inside the screen.
  useSetJobHeaderAction(
    () =>
      isWriting ? (
        <button
          type="button"
          onClick={save}
          disabled={notes.isSaving || !body.trim()}
          className="inline-flex h-11 items-center px-3 text-[13px] font-medium text-brand-ink disabled:opacity-40"
        >
          {notes.isSaving ? "Saving…" : "Save"}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setComposing(true)}
          className="inline-flex h-11 items-center whitespace-nowrap px-3 text-[13px] font-medium text-brand-ink"
        >
          New note
        </button>
      ),
    [isWriting, notes.isSaving, body, editing?.id, editing?.body, draft],
  )

  if (isWriting) {
    return (
      <Composer
        value={body}
        onChange={(value) =>
          editing ? setEditing({ ...editing, body: value }) : setDraft(value)
        }
        onCancel={close}
        footnote={
          editing ? "editing a note" : notes.isEmpty ? "first note on this job" : undefined
        }
      />
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col px-4 @3xl:px-6">
      <div className="flex flex-none items-baseline gap-3.5 py-3.5">
        <span className={META}>
          {notes.total} note{notes.total === 1 ? "" : "s"}
          {notes.search && notes.notes.length !== notes.total
            ? ` · ${notes.notes.length} shown`
            : ""}
        </span>

        {searching || notes.search ? (
          <input
            autoFocus
            value={notes.search}
            onChange={(event) => notes.setSearch(event.target.value)}
            onBlur={() => setSearching(false)}
            placeholder="Search notes"
            aria-label="Search notes"
            className="ml-auto w-40 border-b bg-transparent pb-1 text-right text-[11px] outline-none transition-colors focus:border-primary"
          />
        ) : (
          <button
            type="button"
            onClick={() => setSearching(true)}
            className="ml-auto inline-flex items-center gap-[7px] text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            <Search className="h-[13px] w-[13px]" />
            Search notes
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-6">
        {notes.isLoading ? (
          <p className="flex items-center gap-2 border-t py-3.5 text-[12.5px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading notes…
          </p>
        ) : notes.isError ? (
          <p className="border-t py-3.5 text-[12.5px] text-destructive">
            The notes on this job could not be loaded.
          </p>
        ) : notes.notes.length === 0 ? (
          /* Only reachable through a search that matched nothing — an empty job
             opens the composer instead. */
          <p className="border-t py-3.5 text-[12.5px] text-muted-foreground">
            No note mentions “{notes.search}”.
          </p>
        ) : (
          notes.notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              onEdit={() => setEditing(note)}
              onDelete={() => notes.remove(note.id)}
            />
          ))
        )}
      </div>
    </div>
  )
}

/** A note is its own text, then who wrote it and when. No card. */
function NoteRow({
  note,
  onEdit,
  onDelete,
}: {
  note: JobNote
  onEdit: () => void
  onDelete: () => void
}) {
  const [confirming, setConfirming] = useState(false)

  return (
    <article className="border-t py-[13px]">
      <RichText html={note.body} size="md" />

      <div className="mt-[9px] flex items-baseline gap-3">
        <span className={META}>{note.label}</span>
        <span className={META}>{note.author}</span>
        <span className={META}>{note.when}</span>

        <span className="ml-auto flex items-center gap-3.5">
          {confirming ? (
            <>
              <button
                type="button"
                onClick={onDelete}
                className="font-mono text-[10.5px] leading-none text-destructive"
              >
                delete
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className={cn(META, "hover:text-foreground")}
              >
                keep
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onEdit}
                aria-label="Edit note"
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                <Pencil className="h-[13px] w-[13px]" />
              </button>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                aria-label="Delete note"
                className="text-muted-foreground transition-colors hover:text-destructive"
              >
                <Trash2 className="h-[13px] w-[13px]" />
              </button>
            </>
          )}
        </span>
      </div>
    </article>
  )
}

/**
 * 18b. The field fills the screen, the toolbar sits on a hairline under it, and
 * the only copy is the one thing worth explaining: notes are internal.
 */
function Composer({
  value,
  onChange,
  onCancel,
  footnote,
}: {
  value: string
  onChange: (value: string) => void
  onCancel: () => void
  footnote?: string
}) {
  return (
    <div className="flex h-full min-h-0 flex-col px-4 pt-4 @3xl:px-6">
      <RichTextEditor
        className="flex-1"
        content={value}
        onChange={onChange}
        placeholder="What should the next person on this job know?"
      />

      {/* The state of the note and the way out sit under the field, where they
          do not compete with the toolbar for a phone's width. */}
      <div className="flex flex-none items-center gap-4 border-t py-3">
        {footnote && <span className={cn(META, "whitespace-nowrap")}>{footnote}</span>}
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
        >
          Cancel
        </button>
      </div>

      <p className="flex-none py-3.5 text-[11.5px] leading-[1.6] text-muted-foreground">
        Notes stay inside your team. They never appear on a submission, an invite or
        anything the client sees.
      </p>
    </div>
  )
}
